import { transferLicenseBody, type UserListItem } from '@planuze/admin-contracts';
import { toast } from '@planuze/ui';
import { useEffect, useState } from 'react';
import { useUsersContent } from '../../../content/sections/users.js';
import { useTransferLicense } from '../../../hooks/use-users.js';
import { ReasonDialog } from '../../reason-dialog.js';
import { userErrorMessage } from '../user-error.js';
import { UserPicker } from '../user-picker.js';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly userId: string;
  readonly packId: string;
}

export function TransferLicenseModal({ open, onClose, userId, packId }: Props) {
  const copy = useUsersContent();
  const [target, setTarget] = useState<UserListItem | null>(null);
  const transfer = useTransferLicense(userId);
  useEffect(() => {
    if (!open) setTarget(null);
  }, [open]);
  const confirm = (reason: string): void => {
    if (target === null) return;
    transfer.mutate(
      { toUserId: target.id, packId, reason },
      {
        onSuccess: () => {
          toast.success(copy.transferLicenseModal.success(packId, target.email));
          onClose();
        },
        onError: (err) => {
          toast.error(userErrorMessage(err, copy.errors));
        },
      },
    );
  };
  return (
    <ReasonDialog
      open={open}
      onClose={onClose}
      title={copy.transferLicenseModal.title(packId)}
      description={copy.transferLicenseModal.description}
      confirmLabel={copy.transferLicenseModal.confirmLabel}
      reasonLabel={copy.transferLicenseModal.reasonLabel}
      pending={transfer.isPending}
      validate={(reason) =>
        target !== null &&
        transferLicenseBody.safeParse({ toUserId: target.id, packId, reason }).success
      }
      onConfirm={confirm}
    >
      <div className="space-y-1.5">
        <span className="text-sm text-foreground">
          {copy.transferLicenseModal.destinationLabel}
        </span>
        <UserPicker value={target} onChange={setTarget} excludeId={userId} />
      </div>
    </ReasonDialog>
  );
}
